/* eslint-disable @typescript-eslint/no-unused-vars */
import { ActionIcon, Button, Title, Textarea } from "@mantine/core";
import CustomModal from "./secondary/CustomModal";
import { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { IconClose } from "./IconType";
import GlowingIcon from "./secondary/GlowingIcon";
import { useApi } from "../useApi";
import axios, { AxiosInstance } from "axios";
import { useParams } from "react-router-dom";
// import socket from "../socket";
import { showNotification } from "@mantine/notifications";
import { IconX } from "@tabler/icons-react";
import { UserDto, UserRole } from "../dto/user";

// true : le navigateur envoie les octets directement à Google Drive par blocs
// (session ouverte par le serveur). false : ancien chemin, tout passe par le
// serveur qui renvoie ensuite vers Drive.
const DIRECT_UPLOAD_TO_DRIVE = true;
// Taille des blocs envoyés à Google : multiple de 256 Ko, exigé par l'API
const UPLOAD_CHUNK_SIZE = 16 * 1024 * 1024;
const UPLOAD_MAX_RETRIES = 5;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Google répond "Range: bytes=0-N" avec le dernier octet reçu
const nextOffsetFromRange = (range: unknown, fallback: number) => {
  const match = /bytes=0-(\d+)/.exec(String(range ?? ""));
  return match ? Number(match[1]) + 1 : fallback;
};

interface FileWithPreview extends File {
  preview: string;
  uploadPart: string; // new property to identify the part of upload
  uploadPercentage: number;
}

const FileUpload = ({
  files,
  setFiles,
  uploadPart,
  loading,
}: {
  files: FileWithPreview[];
  setFiles: React.Dispatch<React.SetStateAction<FileWithPreview[]>>;
  uploadPart: string; // new prop to identify the part of upload
  loading: boolean;
}) => {
  const onDrop = useCallback((acceptedFiles: File[]) => {
    for (const file of acceptedFiles) {
      if (file.size > 5 * 1024 * 1024 * 1024) {
        showNotification({
          message: "Taille maximale de fichier dépassée (5Go)",
          icon: <IconX size="1.1rem" />,
          color: "red",
        });
        return;
      }
    }
    setFiles((prevFiles) => {
      // Remove duplicates from acceptedFiles
      const newFiles = acceptedFiles.filter(
        (acceptedFile) =>
          !prevFiles.some(
            (prevFile) =>
              prevFile.name === acceptedFile.name &&
              prevFile.size === acceptedFile.size &&
              prevFile.lastModified === acceptedFile.lastModified
          )
      );
      return [
        ...prevFiles,
        ...newFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
            uploadPart, // mark the file with the part of upload
            uploadPercentage: 0,
          })
        ),
      ];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeFile = (index: number) => {
    const filteredFiles = files.filter(
      (file) => file.uploadPart === uploadPart
    );
    const fileToRemove = filteredFiles[index];
    const indexInOriginalArray = files.indexOf(fileToRemove);
    setFiles(files.filter((_, i) => i !== indexInOriginalArray));
  };

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  return (
    <>
      {!loading && (
        <div
          {...getRootProps()}
          style={{
            padding: "20px",
            border: "1px solid #2E6DA4",
            borderRadius: 12,
            backgroundColor: "#F5FBFF",
            cursor: "pointer",
          }}
        >
          <input {...getInputProps()} />
          <div style={{ display: "flex" }}>
            <GlowingIcon icon="upload" />
            <div>
              <p
                style={{
                  margin: 0,
                  fontWeight: 700,
                  color: "#2C3F53",
                  marginTop: 5,
                }}
              >
                Cliquez pour ajouter, ou glissez et déposez
              </p>
              <p style={{ margin: 0, color: "#656565" }}>
                MP3, MP4, MP4A, WMA, Word, PDF, JPG
              </p>
            </div>
          </div>
        </div>
      )}
      {files
        .filter((file) => file.uploadPart === uploadPart)
        .map((file, index) => (
          <>
            <div key={index} style={{ display: "flex", alignItems: "center" }}>
              <p
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  margin: 0,
                }}
              >
                {file.name}{" "}
              </p>
              <p
                style={{
                  whiteSpace: "nowrap",
                  margin: 0,
                }}
              >
                {" "}
                {file.uploadPercentage > 0 && `(${file.uploadPercentage}%)`}
              </p>
              <ActionIcon
                style={{ marginLeft: 10 }}
                onClick={() => {
                  removeFile(index);
                }}
              >
                {IconClose}
              </ActionIcon>
            </div>
            {file.size > 536870900 && file.uploadPercentage === 100 && (
              <p>
                Traitement en cours d'un fichier volumineux, veuillez patienter
                maximum 1mn.
              </p>
            )}
          </>
        ))}
    </>
  );
};

const UploadModal = ({
  opened,
  setOpened,
  refresh,
  user,
}: {
  opened: boolean;
  setOpened: React.Dispatch<React.SetStateAction<boolean>>;
  refresh: () => Promise<void>;
  user: UserDto | null;
}) => {
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [comment, setComment] = useState<string>("");
  const { folderId, username } = useParams<{
    folderId?: string;
    username?: string;
  }>();
  const api = useApi();

  useEffect(() => {
    // Listen to 'progress' event for all file uploads
    // socket.on(
    //   "progress",
    //   ({ filename, progress }: { filename: string; progress: number }) => {
    //     setFiles((currentFiles) => {
    //       const fileIndex = currentFiles.findIndex(
    //         (file) => file.name === filename
    //       );
    //       if (fileIndex !== -1) {
    //         const newFiles = [...currentFiles];
    //         newFiles[fileIndex].uploadPercentage = progress;
    //         return newFiles;
    //       }
    //       return currentFiles;
    //     });
    //   }
    // );
    // There's no need to close the socket connection when the component unmounts,
    // because it's now managed globally
  }, []);

  const startUpload = async () => {
    if (files.length === 0) return;
    setLoading(true);
    try {
      for (let i = 0; i < files.length; i++) {
        // socket.emit("join", files[i].name);
        // Le dernier fichier porte un signal de fin : le serveur envoie la
        // notification tout de suite au lieu d'attendre le délai de secours.
        const isLastFile = i === files.length - 1;
        if (DIRECT_UPLOAD_TO_DRIVE) {
          await uploadFileDirect(api, files[i], folderId, isLastFile);
        } else {
          await uploadFileViaServer(api, files[i], folderId, isLastFile);
        }
      }
    } catch (err) {
      console.error(err);
      showNotification({
        message:
          "L'envoi a échoué, vérifiez votre connexion puis réessayez. Les fichiers déjà envoyés sont conservés.",
        icon: <IconX size="1.1rem" />,
        color: "red",
      });
      setLoading(false);
      return;
    }
    // Les emails de notification sont envoyés par le serveur une fois les
    // fichiers sur le Drive, même si le client quitte la page.
    setOpenedAndCleanRefresh();
  };

  const setUploadPercentage = (file: FileWithPreview, percent: number) => {
    setFiles((currentFiles) => {
      const fileIndex = currentFiles.findIndex(
        (fileItem) => fileItem.name === file.name
      );
      if (fileIndex !== -1) {
        const newFiles = [...currentFiles];
        newFiles[fileIndex].uploadPercentage = percent;
        return newFiles;
      }
      return currentFiles;
    });
  };

  // Envoi direct vers Google Drive : le serveur ouvre la session, le
  // navigateur envoie les blocs à Google, le serveur vérifie et enregistre.
  // Une coupure réseau reprend au dernier octet reçu par Google.
  const uploadFileDirect = async (
    api: AxiosInstance,
    file: FileWithPreview,
    folderId: string | undefined,
    isLastFile: boolean
  ) => {
    const { data: session } = await api.post<{
      sessionUri: string;
      token: string;
    }>("/googledrive/upload-session", {
      name: file.name,
      size: file.size,
      mimeType: file.type || "application/octet-stream",
      folderId: folderId || "",
      username: username || "",
      comment,
    });

    // Requêtes vers Google : axios "nu", sans l'en-tête Authorization de l'API
    const acceptIncomplete = (status: number) =>
      (status >= 200 && status < 300) || status === 308;
    let start = 0;
    let retries = 0;
    while (start < file.size) {
      const end = Math.min(start + UPLOAD_CHUNK_SIZE, file.size);
      const chunkStart = start;
      try {
        const res = await axios.put(session.sessionUri, file.slice(start, end), {
          headers: { "Content-Range": `bytes ${start}-${end - 1}/${file.size}` },
          validateStatus: acceptIncomplete,
          onUploadProgress: (e) =>
            setUploadPercentage(
              file,
              Math.min(99, Math.round(((chunkStart + e.loaded) * 100) / file.size))
            ),
        });
        start =
          res.status === 308
            ? nextOffsetFromRange(res.headers.range, end)
            : file.size;
        retries = 0;
      } catch (err) {
        retries += 1;
        if (retries > UPLOAD_MAX_RETRIES) throw err;
        await sleep(2000 * retries);
        // Demander à Google où il en est, puis reprendre de là
        const status = await axios.put(session.sessionUri, null, {
          headers: { "Content-Range": `bytes */${file.size}` },
          validateStatus: acceptIncomplete,
        });
        start =
          status.status === 308
            ? nextOffsetFromRange(status.headers.range, chunkStart)
            : file.size;
      }
    }

    const { data: result } = await api.post<{ complete: boolean }>(
      "/googledrive/upload-session/finalize",
      { token: session.token, isLastFile }
    );
    if (!result.complete) {
      throw new Error(`Google Drive reports the upload of ${file.name} as incomplete`);
    }
    setUploadPercentage(file, 100);
  };

  // Ancien chemin : le fichier transite par le serveur, qui l'envoie à Drive
  const uploadFileViaServer = async (
    api: AxiosInstance,
    file: FileWithPreview,
    folderId: string | undefined,
    isLastFile: boolean
  ) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("folderId", folderId || "");
    formData.append("username", username || "");
    formData.append("comment", comment);
    formData.append("isLastFile", String(isLastFile));

    await api.post("/googledrive/upload", formData, {
      headers: {
        "content-type": "multipart/form-data",
        "X-Custom-Filename": encodeURIComponent(file.name),
      },
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total!
        );
        setFiles((currentFiles) => {
          const fileIndex = currentFiles.findIndex(
            (fileItem) => fileItem.name === file.name
          );
          if (fileIndex !== -1) {
            const newFiles = [...currentFiles];
            newFiles[fileIndex].uploadPercentage = percentCompleted;
            return newFiles;
          }
          return currentFiles;
        });
      },
    });
  };

  const setOpenedAndCleanRefresh = async () => {
    await refresh();
    setOpened(false);
    setComment("");
    setTimeout(() => {
      setFiles([]);
      setLoading(false);
    }, 600);
  };

  const setOpenedAndClean = async () => {
    setOpened(false);
    setFiles([]);
    setLoading(false);
    setComment("");
  };

  const list =
    user?.role === "client"
      ? [
          "Ordre du jour de la réunion",
          "Liste des participants",
          "Vos enregistrements",
        ]
      : ["Fichiers à transmettre"];

  return (
    <CustomModal
      opened={opened}
      setOpened={setOpenedAndClean}
      loading={loading}
    >
      <Title order={3} mt={10} mb={10} style={{ fontSize: 20 }} color="#333333">
        Transmettre mes fichiers
      </Title>
      {!loading && user?.role === UserRole.client && (
        <p style={{ color: "#333333", fontWeight: 700, marginBottom: 12 }}>
          Vous pouvez déposer tous vos fichiers dans la même case, quelle que
          soit leur catégorie.
        </p>
      )}
      {list.map((text, index) => (
        <div key={index}>
          {!loading ? (
            <p style={{ color: "#344054", fontWeight: 500, marginBottom: 6 }}>
              {text}
            </p>
          ) : null}
          <FileUpload
            files={files}
            setFiles={setFiles}
            uploadPart={`part${index}`}
            loading={loading}
          />
        </div>
      ))}

      {!loading && user?.role === UserRole.client && (
        <div style={{ marginTop: "1.5em" }}>
          <p style={{ color: "#344054", fontWeight: 500, marginBottom: 6 }}>
            Commentaire (optionnel)
          </p>
          <Textarea
            placeholder="Ajoutez vos commentaires concernant ces fichiers"
            value={comment}
            onChange={(e) => setComment(e.currentTarget.value)}
            minRows={3}
            style={{ width: "100%" }}
          />
        </div>
      )}

      <div
        style={{
          marginTop: "2em",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          style={{ marginRight: "1em", flex: 1, color: "#333333" }}
          onClick={setOpenedAndClean}
          variant="default"
        >
          Annuler
        </Button>
        <Button style={{ flex: 1 }} color="primary" onClick={startUpload}>
          Envoyer
        </Button>
      </div>
    </CustomModal>
  );
};
export default UploadModal;
